import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Banner, Card, Empty, IncidentBadge, PriorityPill, SlaBadge, UnitBadge,
} from '../components/ui';
import { IncidentForm } from '../components/IncidentForm';
import { MapView } from '../components/MapView';
import { StationDispatch } from '../components/StationDispatch';
import { useFlash, useNow, useRealtime, useResource } from '../state/hooks';
import { api, getAccessToken } from '../lib/api';
import { clock, dateTime, duration, elapsed, phone, titleCase } from '../lib/format';
import type { Category, Incident, IncidentEvent, Priority, Unit } from '../lib/types';
import { useAuth } from '../state/AuthContext';

interface DetailResponse {
  incident: Incident;
  events: IncidentEvent[];
  calls: {
    id: string;
    direction: string;
    state: string;
    ani: string | null;
    caller_id_name: string | null;
    answered_at: string | null;
    ended_at: string | null;
    talk_seconds: number | null;
    wait_seconds: number | null;
  }[];
  recordings: {
    id: string;
    call_id: string;
    duration_seconds: number | null;
    started_at: string;
    stopped_at: string | null;
    mime_type: string;
  }[];
}

/**
 * The full record of one incident: the case file a supervisor reads afterwards
 * and the working view while it is live.
 *
 * The timeline is the spine. Every status change, assignment, note, field
 * message and escalation lands in it in order, which is what makes the record
 * defensible when somebody asks six months later why a unit was sent when it
 * was.
 */
export function IncidentDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const now = useNow();
  const [flash, showFlash] = useFlash();
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const detail = useResource<DetailResponse>(id ? `/incidents/${id}` : null);
  const units = useResource<{ units: Unit[] }>(can('dispatch.view') ? '/units' : null);
  const taxonomy = useResource<{ categories: Category[]; priorities: Priority[] }>('/admin/taxonomy');
  const clientConfig = useResource<{ map: { tileUrl: string | null; attribution: string | null } }>('/config');

  const refresh = useCallback(() => detail.reload(), [detail]);
  useRealtime(['incidents', 'units'], (event) => {
    const payload = event.payload as { id?: string; incidentId?: string } | null;
    if (!payload) return refresh();
    if (payload.id === id || payload.incidentId === id) refresh();
  }, refresh);

  const act = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      showFlash('ok', label);
      refresh();
    } catch (err) {
      showFlash('error', (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (detail.error) return <Banner kind="error">{detail.error}</Banner>;
  if (!detail.data) return <Empty>Loading incident…</Empty>;

  const { incident, events, calls, recordings } = detail.data;
  const closed = incident.status === 'closed' || incident.status === 'cancelled';
  const assignedIds = new Set(incident.units.map((unit) => unit.unitId));
  const free = (units.data?.units ?? []).filter(
    (unit) => !assignedIds.has(unit.id) && !['off_duty', 'out_of_service'].includes(unit.status),
  );

  return (
    <div className="stack">
      {flash && <Banner kind={flash.kind}>{flash.message}</Banner>}

      <Card
        title={
          <div className="row">
            <PriorityPill code={incident.priority_code} color={incident.priority_color} level={incident.priority_level} />
            <h1 className="mono">{incident.incident_no}</h1>
            <IncidentBadge status={incident.status} />
            <SlaBadge
              dueAt={incident.sla_due_at}
              breachedAt={incident.sla_breached_at}
              dispatchedAt={incident.dispatched_at}
              now={now}
            />
          </div>
        }
        actions={
          <div className="row tight">
            {can('incidents.edit') && !closed && (
              <button className="btn sm" onClick={() => setEditing((v) => !v)}>
                {editing ? 'Stop editing' : 'Edit'}
              </button>
            )}
            <Link className="btn sm ghost" to="/incidents">Back to list</Link>
          </div>
        }
      >
        {editing && taxonomy.data ? (
          <IncidentForm
            categories={taxonomy.data.categories}
            priorities={taxonomy.data.priorities}
            incident={incident}
            onSaved={() => {
              setEditing(false);
              showFlash('ok', 'Incident updated.');
              refresh();
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <div className="columns two">
            <div className="stack tight">
              <Detail label="Type" value={incident.category_name ?? 'Unclassified'} />
              <Detail label="Location" value={incident.location_address ?? '—'} />
              {incident.location_details && <Detail label="Detail" value={incident.location_details} />}
              <Detail
                label="Caller"
                value={`${incident.caller_name ?? 'Unknown'}${incident.caller_phone ? ` · ${phone(incident.caller_phone)}` : ''}${
                  incident.caller_relationship ? ` (${incident.caller_relationship})` : ''
                }`}
              />
              <Detail label="Raised by" value={`${incident.created_by_name ?? '—'} at ${dateTime(incident.created_at)}`} />
            </div>
            <div className="stack tight">
              <Milestone label="Created" at={incident.created_at} from={null} />
              <Milestone label="Dispatched" at={incident.dispatched_at} from={incident.created_at} />
              <Milestone label="En route" at={incident.first_en_route_at} from={incident.created_at} />
              <Milestone label="On scene" at={incident.first_on_scene_at} from={incident.created_at} />
              <Milestone label="Resolved" at={incident.resolved_at} from={incident.created_at} />
              <Milestone label="Closed" at={incident.closed_at} from={incident.created_at} />
            </div>
          </div>
        )}

        {!editing && (
          <>
            <h4 style={{ marginTop: 14 }}>Narrative</h4>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{incident.narrative || <span className="faint">—</span>}</p>
          </>
        )}
      </Card>

      <div className="columns wide-side">
        <div className="stack">
          <Card title={`Timeline (${events.length})`} flush>
            <ul className="timeline scroll-md" style={{ padding: '8px 14px' }}>
              {events.map((event) => (
                <li key={event.id}>
                  <span className="when">{clock(event.created_at)}</span>
                  <span className="what">
                    <div className="row tight">
                      <strong>{labelForEvent(event)}</strong>
                      {event.to_status && <IncidentBadge status={event.to_status} />}
                    </div>
                    {event.body && <div className="muted" style={{ whiteSpace: 'pre-wrap' }}>{event.body}</div>}
                    <div className="faint">{event.actor_label ?? 'system'}</div>
                  </span>
                </li>
              ))}
            </ul>

            {can('incidents.edit') && !closed && (
              <div className="row" style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
                <input
                  type="text"
                  placeholder="Add to the log…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn"
                  disabled={busy || !note.trim()}
                  onClick={() =>
                    void act('Note added', async () => {
                      await api.post(`/incidents/${incident.id}/notes`, { body: note.trim() });
                      setNote('');
                    })
                  }
                >
                  Add
                </button>
              </div>
            )}
          </Card>

          {calls.length > 0 && (
            <Card title={`Calls (${calls.length})`} flush>
              <table>
                <thead>
                  <tr>
                    <th>From</th>
                    <th>Direction</th>
                    <th>Waited</th>
                    <th>Talk</th>
                    <th>Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => {
                    const recording = recordings.find((r) => r.call_id === call.id);
                    return (
                      <tr key={call.id}>
                        <td>
                          {phone(call.ani)}
                          <div className="faint">{call.caller_id_name ?? ''}</div>
                        </td>
                        <td>{titleCase(call.direction)}</td>
                        <td>{duration(call.wait_seconds)}</td>
                        <td>{duration(call.talk_seconds)}</td>
                        <td>
                          {!recording ? (
                            <span className="faint">—</span>
                          ) : can('calls.record') ? (
                            <RecordingPlayer id={recording.id} seconds={recording.duration_seconds} />
                          ) : (
                            <span className="faint">restricted</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>

        <div className="stack">
          <Card title="Police station">
            <StationDispatch
              incident={incident}
              canDispatch={can('dispatch.assign') && !closed}
              onChanged={refresh}
            />
          </Card>

          <Card title="Units">
            {incident.units.length === 0 ? (
              <div className="stack tight">
                <p className="muted" style={{ margin: 0 }}>Nobody assigned.</p>
                {!can('dispatch.assign') && (
                  <p className="faint" style={{ margin: 0 }}>
                    A dispatcher assigns units from the Dispatch console.
                  </p>
                )}
              </div>
            ) : (
              <ul className="list">
                {incident.units.map((unit) => (
                  <li key={unit.assignmentId}>
                    <div className="item" style={{ cursor: 'default' }}>
                      <span className="grow">
                        <span className="item-title">{unit.callSign}</span>
                        <span className="item-sub">{titleCase(unit.state)} · {elapsed(unit.assignedAt, now)}</span>
                      </span>
                      <UnitBadge status={unit.unitStatus} />
                      {can('dispatch.assign') && !closed && (
                        <button
                          className="btn sm"
                          disabled={busy}
                          onClick={() =>
                            void act(`${unit.callSign} released`, () =>
                              api.post(`/units/${unit.unitId}/release`, { incidentId: incident.id }),
                            )
                          }
                        >
                          Release
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {can('dispatch.assign') && !closed && free.length > 0 && (
              <div className="row" style={{ marginTop: 10 }}>
                <select
                  value=""
                  onChange={(event) => {
                    const unitId = event.target.value;
                    if (!unitId) return;
                    void act('Unit assigned', () =>
                      api.post('/units/assign', { incidentId: incident.id, unitIds: [unitId] }),
                    );
                  }}
                >
                  <option value="">Assign a unit…</option>
                  {free.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.call_sign} — {titleCase(unit.status)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </Card>

          {incident.latitude != null && incident.longitude != null && (
            <Card title="Scene" flush>
              <MapView
                units={units.data?.units ?? []}
                incidents={[incident]}
                center={{ latitude: incident.latitude, longitude: incident.longitude }}
                selectedId={incident.id}
                height={260}
                tileUrl={clientConfig.data?.map.tileUrl}
                attribution={clientConfig.data?.map.attribution}
              />
            </Card>
          )}

          {can('incidents.close') && !closed && (
            <Card title="Resolve">
              <div className="row">
                {incident.status !== 'resolved' && (
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() => void act('Resolved', () =>
                      api.post(`/incidents/${incident.id}/status`, { status: 'resolved' }),
                    )}
                  >
                    Mark resolved
                  </button>
                )}
                {incident.status === 'resolved' && (
                  <button
                    className="btn primary"
                    disabled={busy}
                    onClick={() => void act('Closed', () =>
                      api.post(`/incidents/${incident.id}/status`, { status: 'closed' }),
                    )}
                  >
                    Close case
                  </button>
                )}
              </div>
              <p className="faint" style={{ marginBottom: 0 }}>
                A closed case cannot be edited. Anything still outstanding belongs in the log first.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="faint">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function Milestone({ label, at, from }: { label: string; at: string | null; from: string | null }) {
  const gap = at && from ? Math.round((new Date(at).getTime() - new Date(from).getTime()) / 1000) : null;
  return (
    <div className="row between">
      <span className="faint">{label}</span>
      <span>
        {at ? clock(at) : <span className="faint">—</span>}
        {gap != null && gap > 0 && <span className="faint"> (+{duration(gap)})</span>}
      </span>
    </div>
  );
}

/**
 * Recording playback. The audio endpoint is permission-checked and every
 * retrieval is audited, so the URL is fetched with the access token rather than
 * handed to an <audio src> the browser would request unauthenticated.
 */
function RecordingPlayer({ id, seconds }: { id: string; seconds: number | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await fetch(`/api/calls/recordings/${id}/audio`, {
        headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(res.status === 404 ? 'No audio was captured' : 'Could not load audio');
      setUrl(URL.createObjectURL(await res.blob()));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (error) return <span className="faint">{error}</span>;
  if (url) return <audio controls src={url} style={{ height: 30 }} />;
  return (
    <button className="btn sm" onClick={() => void load()}>
      Play {seconds ? duration(seconds) : ''}
    </button>
  );
}

function labelForEvent(event: IncidentEvent): string {
  switch (event.kind) {
    case 'created': return 'Incident created';
    case 'status': return `Status → ${titleCase(event.to_status)}`;
    case 'assignment': return 'Units assigned';
    case 'assignment_released': return 'Unit released';
    case 'unit_status': return 'Unit status';
    case 'call_attached': return 'Call attached';
    case 'field_message': return 'Field message';
    case 'station_dispatch': return 'Police station';
    case 'escalation': return 'Escalation';
    case 'note': return 'Note';
    default: return titleCase(event.kind);
  }
}
